import { useCallback, useEffect, useState } from 'react';

export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

export interface Invitation {
  id: string;
  email: string;
  status: InvitationStatus;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string | null;
  active: boolean;
  createdAt: string;
}

/** Error from an admin API call — carries the HTTP status + server error code. */
export class AdminApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(code ?? `HTTP ${status}`);
  }
}

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new AdminApiError(response.status, body.error);
  }
  return (response.status === 204 ? null : await response.json()) as T;
};

export const createInvitation = (email: string): Promise<Invitation> =>
  request('/api/invitations', { method: 'POST', body: JSON.stringify({ email }) });
export const resendInvitation = (id: string): Promise<Invitation> =>
  request(`/api/invitations/${id}/resend`, { method: 'POST' });
export const revokeInvitation = (id: string): Promise<Invitation> =>
  request(`/api/invitations/${id}/revoke`, { method: 'POST' });

export const deactivateUser = (id: string): Promise<unknown> =>
  request(`/api/users/${id}/deactivate`, { method: 'POST' });
export const reactivateUser = (id: string): Promise<unknown> =>
  request(`/api/users/${id}/reactivate`, { method: 'POST' });
export const deleteUser = (id: string): Promise<unknown> =>
  request(`/api/users/${id}`, { method: 'DELETE' });

interface ListState<T> {
  items: Array<T>;
  loading: boolean;
  refresh: () => void;
}

const useList = <T>(path: string): ListState<T> => {
  const [items, setItems] = useState<Array<T>>([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(() => {
    setLoading(true);
    request<Array<T>>(path)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [path]);
  useEffect(() => refresh(), [refresh]);
  return { items, loading, refresh };
};

export const useInvitations = (): ListState<Invitation> => useList<Invitation>('/api/invitations');
export const useAdminUsers = (): ListState<AdminUser> => useList<AdminUser>('/api/users');
